import { customFilter } from "../../config/ApiConfig";
import { ClientIdType, DeliveryModes } from "../../types";

export enum EngagementMode {
  PLATFORM = "PLATFORM",
  SUBSCRIBED = "SUBSCRIBED",
  EXPIRED = "EXPIRED",
}

export enum GroupingMode {
  CUSTOMER = "customer",
  SUPPLIER = "supplier",
}

export type GroupingType = `${GroupingMode}`;

export interface OrderData {
  _id: string;
  orderNo: string;
  customer: {
    name: string;
    mobileNumber?: string;
  };
  orderDate: string;
  orderStatus: string;
  totalItems: number;
  totalQuantity: number;
  orderValue: number;
  deliveryMode: string;
  shipmentAddress: {
    addressLine1: string;
    city: string;
    state: string;
  };
  allocatedTo: Array<{
    supplierId: string;
    isActive: boolean;
    supplier?: {
      businessName: string;
      address?: {
        addressLine1: string;
        city: string;
        state: string;
      };
    };
  }>;
  isUrgent: boolean;
  orderETA: { etaGivenTime: string; eta: string; reason: string }[];
  invoiceInfo?: {
    invoiceMediaId?: {
      $oid: string;
    };
    invoiceNo?: string;
    invoiceAmount?: string;
    invoiceDate?: string;
    noOfItems?: number;
    totalQtyOfItems?: number;
    invoiceStatus?: string;
  };
  garaazInvoiceInfo?: {
    invoiceMediaId?: { $oid: string };
    invoiceNo?: string;
    invoiceAmount?: string;
    invoiceDate?: string;
    noOfItems?: number;
    totalQtyOfItems?: number;
    invoiceStatus?: string;
  };
  challanInfo?: {
    challanMediaId?: {
      $oid: string;
    };
    challanNo?: string;
    challanAmount?: string;
    challanDate?: string;
  };
  PPDAmount?: number;
  orderItems?: Array<{
    eligibleVehicleType?: number;
    [key: string]: any;
  }>;
  /** Rider saved at R2D Self Dispatch (order.metaInfo.dispatchRider). */
  dispatchRider?: {
    name: string;
    phone: string;
    vehicleNumber?: string;
    trackingUrl?: string;
  };
}

export interface PorterQuotePayload {
  pickupDetails: {
    lat: number;
    lng: number;
  };
  dropDetails: {
    lat: number;
    lng: number;
  };
  customerDetails: {
    name: string;
    mobileNumber: string;
  };
}

export interface VehicleOption {
  type: string;
  eta: {
    value: number;
    unit: string;
  } | null;
  fare: {
    currency: string;
    minor_amount: number;
  };
  capacity: {
    value: number;
    unit: string;
  };
  size: {
    length: {
      value: number;
      unit: string;
    };
    breadth: {
      value: number;
      unit: string;
    };
    height: {
      value: number;
      unit: string;
    };
  };
}

export interface PorterQuoteResponse {
  vehicles: VehicleOption[];
}

export type RiderDetails = {
  name: string;
  phone: string;
  vehicleNumber?: string;
  trackingUrl?: string;
  id?: string;
};

export interface RefreshConfig {
  enabled: boolean;
  intervalMinutes?: number;
}

export type DispatchDashboardMode = "r2d" | "dispatched";

export interface DispatchDashboardProps {
  baseUrl: string;
  authToken: string;
  clientId?: ClientIdType;
  customFilters?: customFilter;
  riders?: RiderDetails[];
  ridersLoading?: boolean;
  maskDeliveryContactDetails?: boolean;
  userName?: string;
  enableDistanceTabs?: boolean;
  refreshConfig?: RefreshConfig;
  /** r2d = READY_TO_DISPATCH list; dispatched = DISPATCHED list (same UI). */
  dashboardMode?: DispatchDashboardMode;
  dashboardTitle?: string;
}

export interface EntityData {
  _id: string;
  name: string;
  businessName?: string;
  count?: number;
  totalItems?: number;
  totalItemsCount?: number;
  totalQuantity?: number;
  totalOrderItemsQuantity?: number;
  totalValue?: number;
  totalInvoiceAmount?: number;
  orderCount?: number;
  urgentCount?: number;
  urgentOrdersCount?: number;
  minETAdate?: string;
  engagementMode?: string;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  supplierAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  shipmentAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface OrderData {
  _id: string;
  orderNo: string;
  customer: {
    name: string;
    mobileNumber?: string;
  };
  orderDate: string;
  orderStatus: string;
  totalItems: number;
  totalQuantity: number;
  orderValue: number;
  deliveryMode: string;
  shipmentAddress: {
    addressLine1: string;
    city: string;
    state: string;
  };
  allocatedTo: Array<{
    supplierId: string;
    isActive: boolean;
    supplier?: {
      businessName: string;
      address?: {
        addressLine1: string;
        city: string;
        state: string;
      };
    };
  }>;
  isUrgent: boolean;
  invoiceInfo?: {
    invoiceMediaId?: { $oid: string };
    invoiceNo?: string;
    invoiceAmount?: string;
    invoiceDate?: string;
    noOfItems?: number;
    totalQtyOfItems?: number;
    invoiceStatus?: string;
  };
  challanInfo?: {
    challanMediaId?: {
      $oid: string;
    };
    challanNo?: string;
    challanAmount?: string;
    challanDate?: string;
  };
  orderETA: { etaGivenTime: string; eta: string; reason: string }[];
  PPDAmount?: number;
  orderItems?: Array<{
    eligibleVehicleType?: number;
    [key: string]: any;
  }>;
  dispatchRider?: {
    name: string;
    phone: string;
    vehicleNumber?: string;
    trackingUrl?: string;
  };
}

export interface NestedEntityCardProps {
  entity: EntityData;
  entityType: GroupingType;
  parentEntityId: string;
  parentEntityName?: string;
  orders: OrderData[];
  expanded: boolean;
  onToggleExpansion: () => void;
  isLoading?: boolean;
  selectedOrders?: string[];
  onOrderSelectionChange?: (
    orderNo: string,
    isSelected: boolean,
    customerId?: string,
    customerName?: string,
    supplierId?: string,
    supplierName?: string,
    deliveryMode?: DeliveryModes,
    orderId?: string,
    orderData?: any,
  ) => void;
  activeTab?: string;
  collapseSignal?: number;
  /** Dispatched dashboard: show rider allocated at Self Dispatch. */
  showRiderDetail?: boolean;
}

export interface EntityFacet {
  _id: string;
  count: number;
  totalInvoiceAmount?: number;
  totalOrderItemsQuantity?: number;
  totalItemsCount?: number;
  name?: string;
  businessName?: string;
  type?: string;
  mobileNumber?: string;
  urgentOrdersCount?: number;
  minETAdate?: string;
  engagementMode?: string;
  shipmentAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  supplierAddress?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface GroupCardProps {
  entityId: string;
  entityName: string;
  groupingMode: GroupingType;
  expanded: boolean;
  onToggleExpansion: () => void;
  isLoading?: boolean;
  entityFacet?: EntityFacet;
  selectedOrders?: string[];
  onOrderSelectionChange?: (
    orderNo: string,
    isSelected: boolean,
    customerId?: string,
    customerName?: string,
    supplierId?: string,
    supplierName?: string,
    deliveryMode?: DeliveryModes,
    orderId?: string,
    orderData?: any,
  ) => void;
  activeTab?: string;
  collapseSignal?: number;
  showRiderDetail?: boolean;
}
